import React, { useEffect, useState, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '@trqk/components';
import {
    getBuildEnvironment,
    getInLocalStorage,
    setInLocalStorage,
} from '@trqk/utils';
import {
    EnvironmentCheckbox,
    EnvironmentsModal,
    FeatureFlagSetting,
} from './components';
import {
    getFlagForBuildEnvironment,
    isMissingFlagName,
} from '../../../featureFlags';
import { useModal } from '../../../hooks';

const environmentList = ['development', 'staging', 'production'];

interface FeatureFlagConfig {
    constantKey?: string;
    description: ReactNode;
    flagName: FeatureFlagName;
}

const featureFlagConfigs: FeatureFlagConfig[] = [
    {
        description:
            'Show Apollo Client errors in a dialog that persists until the user closes it',
        flagName: 'apolloClientErrorDialog',
    },
    {
        description:
            'If trqkUser.migration_ingestion !== "COMPLETE", prevent user from using app',
        flagName: 'checkForDataMigration',
    },
    {
        description: (
            <p>
                Render links to a filtered view of the performances table from
                within various part of the Gap Analysis workflow (see{' '}
                <a
                    className="underline"
                    href="https://github.com/trqk/trqk-client/pull/758"
                    target="_blank"
                    rel="noreferrer"
                >
                    screenshots
                </a>
                )
            </p>
        ),
        flagName: 'gapAnalysisLinksToPerformancesTable',
    },
    {
        constantKey: 'MAX_STATEMENT_UPLOAD_BATCH_SIZE',
        description: 'Limit the number of concurrent statement ingestions',
        flagName: 'limitStatementUploadBatchSize',
    },
    {
        constantKey: 'MAX_STATEMENT_SIZE_MB',
        description: 'Limit the size of an individual statement upload (in MB)',
        flagName: 'limitStatementUploadFileSize',
    },
    {
        description:
            'Include the License section in the Account > Settings page',
        flagName: 'paywall',
    },
];

const buildEnvironment = getBuildEnvironment();

const defaultFlags: FlagsForEnvironment = {
    apolloClientErrorDialog: getFlagForBuildEnvironment(
        'apolloClientErrorDialog',
        buildEnvironment,
    ),
    checkForDataMigration: getFlagForBuildEnvironment(
        'checkForDataMigration',
        buildEnvironment,
    ),
    clientEventsPage: getFlagForBuildEnvironment(
        'clientEventsPage',
        buildEnvironment,
    ),
    gapAnalysisLinksToPerformancesTable: getFlagForBuildEnvironment(
        'gapAnalysisLinksToPerformancesTable',
        buildEnvironment,
    ),
    limitStatementUploadBatchSize: getFlagForBuildEnvironment(
        'limitStatementUploadBatchSize',
        buildEnvironment,
    ),
    limitStatementUploadFileSize: getFlagForBuildEnvironment(
        'limitStatementUploadFileSize',
        buildEnvironment,
    ),
    paywall: getFlagForBuildEnvironment('paywall', buildEnvironment),
    truncateChartData: getFlagForBuildEnvironment(
        'truncateChartData',
        buildEnvironment,
    ),
};

/**
 * Exposes controls for setting the current environment and feature flags. This feature is only
 * enabled when getEnableControlPanel returns true - see: packages/utils/src/getEnableControlPanel.tsx
 */
export const ControlPanel = (): JSX.Element => {
    const modal = useModal();
    const history = useHistory();
    const [currentEnvironment, setCurrentEnvironment] = useState<string | null>(
        getInLocalStorage<string>('environment') ?? buildEnvironment,
    );

    const setEnvironment = (nextEnvironment: string): void => {
        setInLocalStorage('environment', () => nextEnvironment);
        setCurrentEnvironment(nextEnvironment);
    };

    const [currentFeatureFlags, setCurrentFeatureFlags] =
        useState<FlagsForEnvironment | null>(null);

    const setFeatureFlag = (
        flagName: FeatureFlagName,
        nextEnabled: boolean,
    ): void => {
        setInLocalStorage(
            'featureFlags',
            (prev: FlagsForEnvironment | null): FlagsForEnvironment => {
                let nextFlags = defaultFlags;

                if (prev !== null) {
                    nextFlags = {
                        ...prev,
                        [flagName]: nextEnabled,
                    };
                } else {
                    nextFlags[flagName] = nextEnabled;
                }

                setCurrentFeatureFlags(nextFlags);

                return nextFlags;
            },
        );
    };

    useEffect(() => {
        setInLocalStorage(
            'featureFlags',
            (prev: FlagsForEnvironment | null): FlagsForEnvironment => {
                if (prev === null || isMissingFlagName(prev)) {
                    setCurrentFeatureFlags(defaultFlags);

                    return defaultFlags;
                }

                setCurrentFeatureFlags(prev);

                return prev;
            },
        );

        setInLocalStorage('environment', (prev: Environment | null) => {
            if (prev === null) {
                return buildEnvironment;
            }

            return prev;
        });
    }, []);

    return (
        <div className="m-8">
            <button
                className="mb-4 text-tertiary-400"
                type="button"
                onClick={(): void => {
                    history.goBack();
                }}
            >
                {'< Back'}
            </button>
            <h2>Control Panel</h2>
            <div>
                <h3 className="mt-6 mb-2">Environment</h3>
                <p>
                    When you select a new environment, you will be redirected to
                    the Sign In page to re-authenticate.
                </p>
                <div className="ml-4 mt-2">
                    {environmentList.map(
                        (environment: string): JSX.Element => (
                            <EnvironmentCheckbox
                                key={environment}
                                currentEnvironment={currentEnvironment}
                                setEnvironment={setEnvironment}
                                environment={environment}
                            />
                        ),
                    )}
                    <Button
                        autoWidth={true}
                        addClasses="mt-4"
                        color="primary"
                        handleClick={() => {
                            modal.add({
                                Content: EnvironmentsModal,
                            });
                        }}
                    >
                        Show environment variables
                    </Button>
                </div>
                <h3 className="mt-6 mb-2">Feature Flags</h3>
                <div className="ml-4 mt-2">
                    {featureFlagConfigs.map(
                        ({
                            flagName,
                            constantKey,
                            description,
                        }: FeatureFlagConfig): JSX.Element => (
                            <FeatureFlagSetting
                                key={flagName}
                                constantKey={constantKey}
                                description={description}
                                flagName={flagName}
                                setFeatureFlag={setFeatureFlag}
                                enabled={
                                    currentFeatureFlags?.[flagName] ?? false
                                }
                            />
                        ),
                    )}
                </div>
            </div>
        </div>
    );
};
